import React, { useState } from 'react';
import { Modal, Form, Input, Tag, Space, Alert, notification } from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined, WarningOutlined } from '@ant-design/icons';
import { hazardService } from '@iso/services/hazard.service';
import { getEscalationLevelShortLabel, hazardEscalationLevels } from '@iso/constants/hazardStatus';

const { TextArea } = Input;

/**
 * Manual Escalate Modal
 * Allows user to manually escalate a hazard to the next level with a reason
 */
const ManualEscalateModal = ({ visible, hazardId, hazardTitle, currentLevel, onCancel, onSuccess }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const nextLevel = currentLevel + 1;
  const isLevel3 = nextLevel === 3;

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const result = await hazardService.escalateHazard(
        hazardId,
        values.reason
      );

      if (result && result.code === '0000') {
        notification.success({
          message: 'Success',
          description: `Hazard escalated to Level ${result.new_level || nextLevel}`,
          icon: <ArrowUpOutlined style={{ color: '#1890ff' }} />
        });
        form.resetFields();
        if (onSuccess) onSuccess();
      } else {
        notification.error({
          message: 'Error',
          description: result.message || 'Failed to escalate hazard',
        });
      }
    } catch (error) {
      console.error('Validation failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    if (onCancel) onCancel();
  };

  const getCurrentLevelInfo = () => {
    return hazardEscalationLevels.find(l => l.value === currentLevel);
  };

  const getNextLevelInfo = () => {
    return hazardEscalationLevels.find(l => l.value === nextLevel);
  };

  return (
    <Modal
      title={
        <Space>
          <ArrowUpOutlined />
          <span>Manually Escalate Hazard</span>
        </Space>
      }
      open={visible}
      onOk={handleOk}
      onCancel={handleCancel}
      confirmLoading={loading}
      okText="Escalate Now"
      okButtonProps={{
        icon: <ArrowUpOutlined />,
        danger: isLevel3,
        style: isLevel3 ? {} : { backgroundColor: '#0B2A1B', borderColor: '#0B2A1B', color: '#fff' }
      }}
      width={600}
      destroyOnClose
    >
      <div style={{ marginBottom: 16 }}>
        <p><strong>Hazard:</strong> {hazardTitle}</p>
      </div>

      <Alert
        message="Escalation"
        description={
          <Space direction="vertical" style={{ width: '100%' }}>
            <Space>
              <span>Current Level:</span>
              <Tag color={currentLevel === 3 ? 'red' : currentLevel === 2 ? 'orange' : 'blue'}>
                {getEscalationLevelShortLabel(currentLevel)}
              </Tag>
              <span>{getCurrentLevelInfo()?.description}</span>
            </Space>
            <Space>
              <ArrowDownOutlined />
            </Space>
            <Space>
              <span>Next Level:</span>
              <Tag color={nextLevel === 3 ? 'red' : nextLevel === 2 ? 'orange' : 'blue'}>
                {getEscalationLevelShortLabel(nextLevel)}
              </Tag>
              <span>{getNextLevelInfo()?.description}</span>
            </Space>
            {isLevel3 && (
              <Alert
                type="warning"
                message="URGENT: Level 3 Escalation"
                description="Escalating to Level 3 will trigger Email + SMS + Push notifications to the Group Safety Manager."
                icon={<WarningOutlined />}
                showIcon
                style={{ marginTop: 8 }}
              />
            )}
          </Space>
        }
        type={isLevel3 ? 'error' : 'info'}
        showIcon
        style={{ marginBottom: 16 }}
      />

      <Form
        form={form}
        layout="vertical"
        preserve={false}
      >
        <Form.Item
          name="reason"
          label="Reason for Manual Escalation"
          rules={[
            {
              required: true,
              whitespace: true,
              message: 'Please provide a reason for manual escalation'
            },
            {
              min: 15,
              message: 'Reason must be at least 15 characters'
            }
          ]}
        >
          <TextArea
            rows={4}
            placeholder="Explain why this hazard needs immediate escalation to the next level..."
            maxLength={500}
            showCount
          />
        </Form.Item>

        <Alert
          message="Note"
          description="Manual escalation will create a new assignment at the next level and reset the deadline to 2 business days from now."
          type="info"
          showIcon
        />
      </Form>
    </Modal>
  );
};

export default ManualEscalateModal;
